define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/_base/window",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/fx/easing",
    "dojo/keys",
    "dojo/mouse",
    "dojo/on",
    "dojo/query",
    "dojo/touch",
    "dijit/focus",
    "dijit/registry",
    "dijit/layout/ContentPane",
    "mw-dialogs/controller/Resizable",
    "mw-form/ContextMenu",
    "mw-form/MenuItem",
    "mw-form/MenuSeparator",
    "mw-form/ToggleButton",
    "./ActionsMenu",
    "./AnimationTiming",
    "./ButtonBar",
    "./Geometry",
    "./LayoutChildProperty",
    "./LayoutContainerEvent",
    "./LayoutUtils",
    "./SelectionBlocker",
    "./TitledPane",
    "dojo/i18n!l10n/nls/layout",
    "MW/layout/SelectionManager"
], function (declare, event, lang, window, dom, domAttr, domClass, domConstruct, domGeometry, domStyle, easing,
             keys, mouse, on, query, touch, focus, registry, ContentPane, Resizable, ContextMenu, MenuItem, MenuSeparator,
             ToggleButton, ActionsMenu, AnimationTiming, ButtonBar, Geometry, LayoutChildProperty, LayoutContainerEvent,
             LayoutUtils, SelectionBlocker, TitledPane, layoutL10n, SelectionManager) {

    var closeClickExclusion;

    /**
     * Defines a widget that displays buttons which trigger the display of corresponding children on top of other page content.
     */
    var BorderBar = declare([ButtonBar], {

        /* _childInfo: an array containing the following per child.  The ordering of the array reflects the ordering of
                       the buttons (which is not necessarily the same as the DOM element order).
         properties
         button
         childLender
         widget
         onTopPane
         showTitleBar
         animationState
             isExpanding
             initialPosition
             finalPosition
             currentBox
             startTime
             duration
             doneHandler
         */
        animationEnabled: true,
        fixParallelDimension: true,
        defaultExpandSize: 300,
        expandButtonIdPrefix: "",

        constructor: function () {
            this._childInfo = [];
        },

        postMixInProperties: function () {
            this.inherited(arguments);
            if (this.region === "left" || this.region === "right") {
                this.orientation = "vertical";
            }
            this.dragOffEdge = Geometry.oppositeRegion(this.region);
            this._restrictedEdges = this._computerestrictedEdges();
        },

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "borderBar");
            domClass.add(this.domNode, this.orientation + "Bar");
            domClass.add(this.domNode, this.region + "Bar");
            if (this.orientation === "vertical") {
                domStyle.set(this.domNode, {width: "22px"});
                // Grandfather obsolete CSS classes in case tests depend on them
                domClass.add(this.domNode, this.region + "SideBar");
            }

            this.own(on(this.ownerDocument, touch.press, lang.hitch(this, "_handleTouch")));
            LayoutUtils.preventDefaultContextMenu(this.domNode);
            LayoutUtils.setLocaleOnDomNode(this.domNode);

            // Add expand button
            var expandAllButton = domConstruct.create("div", {id : this.expandButtonIdPrefix + this.region + "_expandAllButton"});
            domClass.add(expandAllButton, "expandButton");
            domAttr.set(expandAllButton, "title", this.orientation === "vertical" ? layoutL10n.expandSidePanel : layoutL10n.expandBottomPanel);
            dom.setSelectable(expandAllButton, false);
            LayoutUtils.preventFocus(expandAllButton);
            var expandFunction =  lang.hitch(this, "_expandAll");
            on(expandAllButton, "click", expandFunction);
            on(this.domNode, "dblclick", expandFunction);
            expandAllButton.tabIndex = -1;
            this.own(on(expandAllButton, "keyup", lang.hitch(this, "_expandAllButtonKeyUpHandler")));
            this.own(on(expandAllButton, "keydown", lang.hitch(this, "_expandAllButtonKeyDownHandler")));
            this.own(on(expandAllButton, "focus", function () {
                expandAllButton.classList.add("expandButtonFocused");
            }));
            this.own(on(expandAllButton, "blur", function () {
                expandAllButton.classList.remove("expandButtonFocused");
            }));
            this.own(on(this.domNode, "keydown", lang.hitch(this, "_keyDownHandler")));
            this.setPostNeighbor(expandAllButton);
            this._createContextMenu();
        },

        startup: function () {
            if (this._started) {return; }
            this.inherited(arguments);

            // Acquire the selection manager
            var widget = this;
            while (widget && !this.selectionManager) {
                this.selectionManager = widget.selectionManager;
                widget = widget.getParent();
            }
        },

        acceptLoan: function (childLender, properties, skipButtonAdd) {
            var buttonProperties = {
                text: properties.shortTitle || properties.title
            };
            buttonProperties.label = buttonProperties.text;  // label is basis for ButtonBar tooltip
            if (properties.description) {
                buttonProperties.title = properties.description;
            }
            var childInfo = {
                properties: properties,
                childLender: childLender,
                button: new ToggleButton(buttonProperties)
            };
            domClass.add(childInfo.button.domNode, "mwCollapsedPanelButton");
            childInfo.button.disarm = lang.hitch(childInfo.button, function () {
                this.set("disabled", true);
            });
            childInfo.button.rearm = lang.hitch(childInfo.button, function () {
                this.set("disabled", false);
            });
            childInfo.button.childInfo = childInfo;
            if (properties.setId) {
                domClass.add(childInfo.button.domNode, "contextualButton");
            }
            LayoutUtils.preventFocus(childInfo.button.domNode);
            domStyle.set(childInfo.button.domNode, {position: "absolute"});
            this._updateTitleBarVisibility(childInfo);
            this._bindChildProperties(childInfo, properties);
            this._childInfo.push(childInfo);
            if (!skipButtonAdd) {
                this.addButton(childInfo.button, properties);
            }
            childInfo.button.on("change", function () {
                if (childInfo.button.checked) {
                    // Delay a bit in case the user double-clicks.  This avoids starting individual panel expand only
                    // to have it cut short by an expand all.
                    setTimeout(function () {
                        properties.set(LayoutChildProperty.IS_SELECTED, true);
                    }, 150);
                } else {
                    properties.set(LayoutChildProperty.IS_SELECTED, false);
                }
            });
            // When the panel is collapsed, this association to the button will be useful to guide the focus to the button
            if (childInfo.properties.widget) {
                childInfo.properties.widget.button = childInfo.button;
            }
            this.own(on(childInfo.button.domNode, "keydown", lang.hitch(this, "_buttonKeyDownHandler", childInfo)));
            this.own(on(childInfo.button.domNode, "keyup", lang.hitch(this, "_buttonKeyUpHandler", childInfo)));
            this.own(on(childInfo.button.domNode, "focus", function () {
                childInfo.button.domNode.classList.add("borderBarButtonFocused");
            }));
            this.own(on(childInfo.button.domNode, "blur", function () {
                childInfo.button.domNode.classList.remove("borderBarButtonFocused");
            }));
        },

        cancelLoan: function (properties) {
            var index = this._getIndexFromProperties(properties);
            if (index >= 0) {
                var childInfo = this._childInfo[index];
                if (childInfo.onTopPane) {
                    this._completeCollapse(childInfo);
                }
                var button = childInfo.button;

                delete button.onChange; // Don't respond to "last" change triggered by button destroy
                this.removeButton(childInfo.button, function () {
                    button.destroyRecursive();
                }, true);
                childInfo.unbindChildProperties();
                this._childInfo.splice(index, 1);
            }
        },

        removeAll: function () {
            var saveAnimation = this.animationEnabled;
            this.animationEnabled = false;
            this._isRemovingAll = true;
            while (this._childInfo.length > 0) {
                this.cancelLoan(this._childInfo[0].properties);
            }
            this._isRemovingAll = false;
            this.animationEnabled = saveAnimation;
        },

        destroyRecursive: function () {
            this.removeAll();
            this.inherited(arguments);
        },

        dragChildIn: function (childLender, dragOutData, mousePoint) {
            this.acceptLoan(childLender, dragOutData.properties, true);
            var index = this._getIndexFromProperties(dragOutData.properties);
            var childInfo = this._childInfo[index];
            this.dragIn(childInfo.button, childInfo.properties,
                        mousePoint ? mousePoint : dragOutData.index);
        },

        collapseAllChildrenImmediately: function () {
            var that = this;
            // Immediately collapse any children that are expanded or partially expanded
            if (this._animationTimer) {
                clearInterval(this._animationTimer);
                delete this._animationTimer;
            }
            this._childInfo.forEach(function (childInfo) {
                if (childInfo.onTopPane) {
                    that._completeCollapse(childInfo);
                }
                if (childInfo.animationState) {
                    delete childInfo.animationState;
                }

                // while expanding the side container, resetting the navAssociation correctly.
                if (childInfo.properties && childInfo.properties.widget
                    && childInfo.properties.widget.sideBarExpandedNavAssociation) {
                    childInfo.properties.widget.navAssociation = childInfo.properties.widget.sideBarExpandedNavAssociation;
                }
            });
            this._childInfo.forEach(function (childInfo) {
                childInfo.button.set("checked", false);
            });
        },

        _bindChildProperties: function (childInfo, properties) {
            var that = this;
            var watches = [];

            // Check for existence of watch rather than instanceof Stateful since the later check may fail if
            // properties are used across window contexts.
            if (properties.watch && properties.set && properties.get) {
                watches.push(properties.watch(LayoutChildProperty.TITLE, function () {
                    // Effect title change
                    that._setTitleFromProperties(childInfo);
                    that._updateTitleBarVisibility(childInfo);
                }));
                watches.push(properties.watch(LayoutChildProperty.SHORT_TITLE, function () {
                    // Effect title change
                    that._setTitleFromProperties(childInfo);
                    that._updateTitleBarVisibility(childInfo);
                }));
                watches.push(properties.watch(LayoutChildProperty.DESCRIPTION, function (name, oldValue, newValue) {
                    // Effect tooltip change
                    childInfo.button.set("title", newValue);
                }));
                watches.push(properties.watch(LayoutChildProperty.IS_SELECTED, function (name, oldValue, newValue) {
                    if (that.getParent()) {
                        // Update toggle button state
                        childInfo.button.set("checked", newValue);
                        // Effect collapse change
                        if (newValue && !that._isExpandedOrExpanding(childInfo) && !that.selectionManager.shouldBlockRevealChild()) {
                            that._expand(childInfo);
                        } else if (!newValue && !that._isCollapsedOrCollapsing(childInfo)) {
                            that._collapse(childInfo);
                        }
                    }
                }));
                childInfo.unbindChildProperties = function () {
                    watches.forEach(function(watch) {
                        watch.remove();
                    });
                    delete childInfo.unbindChildProperties;
                };
            }
        },

        _setTitleFromProperties: function (childInfo) {
            childInfo.button.set("text", childInfo.properties.shortTitle || childInfo.properties.title);
        },

        _createPrototypeButton: function () {
            var button = new ToggleButton({
                id: this.id +  "prototype",
                text: "Prototype"
            });
            domClass.add(button.domNode, "mwCollapsedPanelButton");
            return button;
        },

        _updateButtonIndices: function () {
            if (!this._isRemovingAll) {
                this.inherited(arguments);
            }
        },

        _getIndexFromProperties: function (properties) {
            var i;
            for (i = 0; i < this._childInfo.length; i++) {
                if (this._childInfo[i].properties === properties) {
                    return i;
                }
            }
            return -1;
        },

        _collapse: function (childInfo) {
            var currentTime, currentSize;
            if (!this._isCollapsedOrCollapsing(childInfo)) {
                // Setup done handler
                var doneHandler = lang.hitch(this, "_completeCollapse", childInfo);
                if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                    doneHandler();
                } else {
                    currentTime = new Date().valueOf();
                    if (!childInfo.animationState) {
                        childInfo.animationState = {};
                        childInfo.animationState.currentBox = domGeometry.getMarginBox(childInfo.onTopPane.domNode);
                    }
                    if (this.orientation === "vertical") {
                        currentSize = childInfo.animationState.currentBox.w;
                        childInfo.animationState.initialPosition = childInfo.animationState.currentBox.l;
                        childInfo.animationState.finalPosition = childInfo.animationState.initialPosition + (this.region === "left" ? -currentSize : currentSize);
                    } else {
                        currentSize = childInfo.animationState.currentBox.h;
                        childInfo.animationState.initialPosition = childInfo.animationState.currentBox.t;
                        childInfo.animationState.finalPosition = childInfo.animationState.initialPosition + (this.region === "top" ? -currentSize : currentSize);
                    }
                    childInfo.animationState.isExpanding = false;
                    childInfo.animationState.startTime = currentTime;
                    childInfo.animationState.duration = AnimationTiming.computeDuration(Math.abs(childInfo.animationState.finalPosition - childInfo.animationState.initialPosition));
                    childInfo.animationState.doneHandler = doneHandler;

                    if (!this._animationTimer) {
                        this._animationTimer = setInterval(lang.hitch(this, "_updateOnTopPositions"), AnimationTiming.UPDATE_INTERVAL);
                    }
                }
            }
        },

        _completeCollapse: function (childInfo) {
            LayoutUtils.saveScrollPosition(childInfo.widget, childInfo.properties);
            this._hideChild(childInfo);
            this._removeChildFocusOverride(childInfo);
            childInfo.childLender.returnChild(childInfo.properties, childInfo.widget);
            delete childInfo.widget;
        },

        _expand: function (childInfo) {
            var expandedBox, initialBox, currentTime;
            var isVertical = this.orientation === "vertical";
            // If a different child is expanded or expanding trigger its collapse
            this._deselectAll(childInfo);

            if (!this._isExpandedOrExpanding(childInfo)) {
                // Might be collapsing in which case on top pane is already present
                if (!childInfo.onTopPane) {
                    childInfo.widget = childInfo.childLender.borrowChild(childInfo.properties);
                    this._overrideChildFocus(childInfo);
                    this._showChild(childInfo);
                    LayoutUtils.restoreScrollPosition(childInfo.widget, childInfo.properties);
                }
                expandedBox = this._computeExpandedBox(childInfo);
                var doneHandler = lang.hitch(this, "_completeExpand", childInfo);
                if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                    this._positionChild(childInfo, expandedBox);
                    doneHandler();
                } else {
                    currentTime = new Date().valueOf();

                    if (childInfo.animationState) {
                        // Currently collapsing, change direction
                        childInfo.animationState.initialPosition = isVertical ? childInfo.animationState.currentBox.l
                                                                              : childInfo.animationState.currentBox.t;
                    } else {
                        childInfo.animationState = {};

                        if (isVertical) {
                            childInfo.animationState.initialPosition = expandedBox.l + (this.region === "left" ? -expandedBox.w : expandedBox.w);
                            childInfo.animationState.finalPosition = expandedBox.l;
                            initialBox = lang.clone(expandedBox);
                            initialBox.l = childInfo.animationState.initialPosition;
                        } else {
                            childInfo.animationState.initialPosition = expandedBox.t + (this.region === "top" ? -expandedBox.h : expandedBox.h);
                            childInfo.animationState.finalPosition = expandedBox.t;
                            initialBox = lang.clone(expandedBox);
                            initialBox.t = childInfo.animationState.initialPosition;
                        }
                        this._positionChild(childInfo, initialBox);
                        childInfo.animationState.currentBox = initialBox;
                    }
                    childInfo.animationState.isExpanding = true;
                    childInfo.animationState.startTime = currentTime;
                    childInfo.animationState.duration = AnimationTiming.computeDuration(Math.abs(childInfo.animationState.finalPosition - childInfo.animationState.initialPosition));
                    childInfo.animationState.doneHandler = doneHandler;

                    if (!this._animationTimer) {
                        this._animationTimer = setInterval(lang.hitch(this, "_updateOnTopPositions"), AnimationTiming.UPDATE_INTERVAL);
                        
                        // g1975363: Expand animation prevents the Editor from properly setting the curso position
                        this._animationCompleteListener = function () {
                            if (childInfo) {
                                // Call focus methods after the animation
                                this._focusChild(childInfo);
                            }
                            
                            // Cleanup the animation listener
                            this.domNode.removeEventListener("animationcomplete", this._animationCompleteListener);
                            delete this._animationCompleteListener;
                        }.bind(this);
                        
                        // Create an 'animationcomplete' listener (instead of a simple method) to maintain the scope of this particular call to _expand
                        this.domNode.addEventListener("animationcomplete", this._animationCompleteListener);
                    }
                }
            }
        },


        _completeExpand: function (childInfo) {
            if (childInfo.focusPending) {
                this._focusChild(childInfo);
                childInfo.focusPending = false;
            }
        },

        _computeExpandedBox: function (childInfo) {
            var barBox = domGeometry.position(this.domNode);
            barBox.l = barBox.x;
            barBox.t = barBox.y;
            var buttonBox = domGeometry.position(childInfo.button.domNode);
            buttonBox.l = buttonBox.x;
            buttonBox.t = buttonBox.y;
            var childProperties = childInfo.properties;
            var expandedBox = {
                l: barBox.x,
                t: barBox.y,
                w: childProperties.__recentWidth ? childProperties.__recentWidth :
                    (childProperties.preferredWidth ? childProperties.preferredWidth : 400),
                h: childProperties.__recentHeight ? childProperties.__recentHeight :
                    (childProperties.preferredHeight ? childProperties.preferredHeight : 400)
            };

            if (this.orientation === "vertical") {
                if (this.region === "left") {
                    expandedBox.l += barBox.w;
                } else {
                    expandedBox.l -= expandedBox.w;
                }
                if (this.fixParallelDimension) {
                    // Match height to bar
                    expandedBox.h = barBox.h;
                } else {
                    // Constrain height to bar and center vertically on button
                    if (expandedBox.h > barBox.h) {
                        expandedBox.h = barBox.h;
                    }
                    expandedBox.t = buttonBox.t + (buttonBox.h - expandedBox.h) / 2;
                    if (expandedBox.t < barBox.t) {
                        expandedBox.t = barBox.t;
                    } else if ((expandedBox.t + expandedBox.h) > (barBox.t + barBox.h)) {
                        expandedBox.t = barBox.t + barBox.h - expandedBox.h;
                    }
                }
            } else {
                if (this.region === "top") {
                    expandedBox.t += barBox.h;
                } else {
                    expandedBox.t -= expandedBox.h;
                }
                if (this.fixParallelDimension) {
                    // Match width to bar
                    expandedBox.w = barBox.w;
                } else {
                    // Constrain width to bar and center horizontally on button
                    if (expandedBox.w > barBox.w) {
                        expandedBox.w = barBox.w;
                    }
                    expandedBox.l = buttonBox.l + (buttonBox.w - expandedBox.w) / 2;
                    if (expandedBox.l < barBox.l) {
                        expandedBox.l = barBox.l;
                    } else if ((expandedBox.l + expandedBox.w) > (barBox.l + barBox.w)) {
                        expandedBox.l = barBox.l + barBox.w - expandedBox.w;
                    }
                }
            }
            return expandedBox;
        },

        _computeClipBox: function (onTopBox) {
            var barBox = domGeometry.position(this.domNode);
            var clipBox;
            var limit;
            switch (this.region) {
                case "left":
                    limit = barBox.x + barBox.w;
                    if (onTopBox.l < limit) {
                        clipBox = {l: limit - onTopBox.l, t: 0, w: onTopBox.w, h: onTopBox.h};
                    }
                    break;
                case "right":
                    limit = barBox.x - onTopBox.l;
                    if (onTopBox.w > limit) {
                        clipBox = {l: 0, t: 0, w: limit, h: onTopBox.h};
                    }
                    break;
                case "top":
                    limit = barBox.y + barBox.h;
                    if (onTopBox.t < limit) {
                        clipBox = {l: 0, t: limit - onTopBox.t, w: onTopBox.w, h: onTopBox.h};
                    }
                    break;
                case "bottom":
                    limit = barBox.y - onTopBox.t;
                    if (onTopBox.h > limit) {
                        clipBox = {l: 0, t: 0, w: onTopBox.w, h: limit};
                    }
            }
            return clipBox;
        },

        _expandAll: function () {
            this.collapseAllChildrenImmediately();
            this.emit(LayoutContainerEvent.EXPAND_ALL);
        },

        _overrideChildFocus: function (childInfo) {
            // Prevent the child widget from being given focus during animation because doing so can cause the entire
            // document body to shift (see geck 1285306).
            var that = this;
            childInfo.originalFocus = childInfo.widget.focus;
            childInfo.widget.focus = function () {
                if (childInfo.animationState) {
                    childInfo.focusPending = true;
                    } else {
                    that._focusChild(childInfo);
                }
            }
        },

        _removeChildFocusOverride: function (childInfo) {
            childInfo.focusPending = false;
            if (childInfo.originalFocus) {
                childInfo.widget.focus = childInfo.originalFocus;
            } else {
                delete childInfo.widget.focus;
            }
        },

        _focusChild: function (childInfo) {
            // Focus should not go to the content if we are blocking it.
            var shouldBlockFocus = this.selectionManager ? this.selectionManager.shouldBlockFocus() : true;

            if (!shouldBlockFocus) {
                // TODO: Remove this one condition after code freeze 19b
                if (childInfo && childInfo.widget && childInfo.widget.refocus) {
                    childInfo.widget.refocus();
                } else if (childInfo && childInfo.originalFocus) {
                    lang.hitch(childInfo.widget, childInfo.originalFocus)();
                } else if (childInfo && childInfo.widget) {
                    focus.focus(childInfo.widget.domNode);
                }
            }
        },

        _showChild: function (childInfo) {
            var onTopId = childInfo.widget.id + "OnTopPane";
            childInfo.onTopPane = new TitledPane({
                    id : onTopId,
                    contentWidget: childInfo.widget,
                    contentProperties: childInfo.properties,
                    showTitleBar : childInfo.showTitleBar});
            childInfo.onTopPane.parentBar = this;
            childInfo.onTopPane.placeAt(window.body());
            childInfo.onTopPane.startup();

            // Set positioning and z-order
            domStyle.set(childInfo.onTopPane.domNode, "position", "absolute");
            domClass.add(childInfo.onTopPane.domNode, "onTopPane");

            // Add resize support
            Resizable.enable({
                widget: childInfo.onTopPane,
                bindingNode: this.getParent().domNode
            });
            this._hideIrrelevantResizeHandles(childInfo.onTopPane);
            this._blockSelectionDuringResize(childInfo.onTopPane);
            childInfo.onTopPane.on("resize", function(event) {
                childInfo.onTopPane.resize(event.dimension);
                childInfo.properties.__recentWidth = event.dimension.w;
                childInfo.properties.__recentHeight = event.dimension.h
            });

            childInfo.properties.set(LayoutChildProperty.IS_SHOWING, true);

            if (childInfo.button && childInfo.button.domNode) {
                childInfo.button.domNode.tabIndex = -1;
                if (childInfo.properties && childInfo.properties.widget) {
                    childInfo.properties.widget.navAssociation = childInfo.button.domNode;
                }
            }
        },

        _hideChild: function (childInfo) {
            window.body().removeChild(childInfo.onTopPane.domNode);
            childInfo.onTopPane.destroy();
            delete childInfo.onTopPane;
            childInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
        },

        _positionChild: function (childInfo, box) {
            var clipBox, coverBox;
            if (childInfo.onTopPane) {
                // Apply clipping if necessary
                clipBox = this._computeClipBox(box);
                domStyle.set(childInfo.onTopPane.domNode, clipBox ? LayoutUtils.formatClipStyle(clipBox) : {clip: "auto"});
                childInfo.onTopPane.resize(box);
            }
        },

        _isExpandedOrExpanding: function (childInfo) {
            return childInfo.onTopPane && !this._isCollapsing(childInfo);
        },

        _isCollapsedOrCollapsing: function (childInfo) {
            return !childInfo.onTopPane || this._isCollapsing(childInfo);
        },

        _isExpanding: function (childInfo) {
            return childInfo.animationState && childInfo.animationState.isExpanding;
        },

        _isCollapsing: function (childInfo) {
            return childInfo.animationState && !childInfo.animationState.isExpanding;
        },

        _updateOnTopPositions: function () {
            var continueAnimating = false;
            var currentTime = new Date().valueOf();
            var animationState, elapsedTime, box, position;
            var that = this;
            var isVertical = this.orientation === "vertical";
            this._childInfo.forEach(function (childInfo) {
                if (childInfo.animationState) {
                    animationState = childInfo.animationState;
                    elapsedTime = currentTime - animationState.startTime;
                    box = animationState.currentBox;

                    if (elapsedTime < animationState.duration) {
                        position = animationState.initialPosition + (animationState.finalPosition - animationState.initialPosition)
                                                                    * easing.cubicInOut(elapsedTime / animationState.duration);
                        if (isVertical) {
                            box.l = position;
                        } else {
                            box.t = position;
                        }
                        continueAnimating = true;
                    } else {
                        if (isVertical) {
                            box.l = animationState.finalPosition;
                        } else {
                            box.t = animationState.finalPosition;
                        }
                        if (animationState.doneHandler) {
                            animationState.doneHandler();
                        }
                        delete childInfo.animationState;
                        // Perform the focus request that was blocked during animation
                        if (that._isExpandedOrExpanding(childInfo)) {
                            childInfo.widget.focus();
                        }
                    }
                    that._positionChild(childInfo, box);
                }
            });
            if (!continueAnimating) {
                clearInterval(this._animationTimer);
                delete this._animationTimer;
                
                // Dispatch an 'animationcomplete' event
                var event = new CustomEvent("animationcomplete");
                this.domNode.dispatchEvent(event);
            }
        },

        _updateTitleBarVisibility: function (childInfo) {
            var showTitleBar = !!(childInfo.properties.shortTitle &&
                                  childInfo.properties.shortTitle !== childInfo.properties.title);
            if (showTitleBar !== childInfo.showTitleBar) {
                childInfo.showTitleBar = showTitleBar;
                if (childInfo.button) {
                    if (showTitleBar) {
                        domClass.remove(childInfo.button.domNode, "mwSurrogateTitle");
                    } else {
                        domClass.add(childInfo.button.domNode, "mwSurrogateTitle");
                    }
                }
            }
        },

        _handleTouch: function (e) {
            // If the touch is not inside the bar or the expanded child then collapse it.
            if (LayoutUtils.isPopupShowing()) {
                // But not if a popup is showing
                return;
            }
            var expandedChildInfo = this._getExpandedOrExpandingInfo();
            if (expandedChildInfo &&
                !dom.isDescendant(e.target, expandedChildInfo.onTopPane.domNode) &&
                !dom.isDescendant(e.target, this.domNode) &&
                !(closeClickExclusion && dom.isDescendant(e.target, closeClickExclusion))) {
                this._deselectAll();
                event.stop(e);
            }
        },

        /*
         * Overridden to stop the event.  If the event isn't stopped problems occur when expanding a collapsed
         * side panel (see geck 1368111).
         */
        _beginButtonDrag: function (buttonInfo, e) {
            this.inherited(arguments);
            event.stop(e);
        },

        _getExpandedOrExpandingInfo: function () {
            var i, childInfo;
            for (i = 0; i < this._childInfo.length; i++) {
                childInfo = this._childInfo[i];
                if (this._isExpandedOrExpanding(childInfo)) {
                    return childInfo;
                }
            }
            return null;
        },

        _deselectAll: function (exceptInfo) {
            var that = this;
            this._childInfo.forEach(function (childInfo) {
                if (childInfo !== exceptInfo && that._isExpandedOrExpanding(childInfo)) {
                    childInfo.properties.set(LayoutChildProperty.IS_SELECTED, false);
                }
            });
        },


        // When a button is dragged off, tweak the information supplied to include the child
        // rather than the button.
        _emitDragOffEvent: function (dragOffInfo) {

            var index = this._getIndexFromProperties(dragOffInfo.properties);
            var childInfo = this._childInfo[index];
            dragOffInfo.widget = childInfo.widget;
            dragOffInfo.box = domGeometry.getMarginBox(this.domNode);
            if (this.orientation === "horizontal") {
                dragOffInfo.box.h = childInfo.properties.__recentHeight || this.defaultExpandSize;
            } else {
                dragOffInfo.box.w = childInfo.properties.__recentWidth || this.defaultExpandSize;
            }
            this.emit(LayoutContainerEvent.DRAG_OUT, dragOffInfo);
        },

        resize: function () {
            var that = this;
            this.inherited(arguments);
            this._childInfo.forEach(function (childInfo) {
                if (childInfo.onTopPane) {
                    that._positionChild(childInfo, that._computeExpandedBox(childInfo));
                }
            });
        },

        _measureButton: function (buttonInfo) {
            if (this.orientation === "horizontal") {
                return this.inherited(arguments);
            }
            var prototype = this._getPrototypeButton();
            prototype.set("text", buttonInfo.button.get("text"));
            var box = domGeometry.getMarginBox(prototype.domNode);
            // Swap width and height to account for transform
            box.h = box.w;
            box.w = 22;
            return box;
        },

        _transformClipBox: function (clipBox, buttonBox) {
            // The clip needs to be relative to the original element box, prior to transformation.
            // The following reverses the CSS transform.
            if (this.orientation === "vertical") {
                clipBox.t = clipBox.l;
                clipBox.l = buttonBox.h - clipBox.h;
                var saveW = clipBox.w;
                clipBox.w = clipBox.h;
                clipBox.h = saveW;
            }
        },

        _hideIrrelevantResizeHandles: function (onTopPane) {
            var handlesToHide = {};
            this._restrictedEdges.forEach(function (edge) {
                switch (edge) {
                    case "north":
                        handlesToHide[".mwResizable-n"] = true;
                        handlesToHide[".mwResizable-ne"] = true;
                        handlesToHide[".mwResizable-nw"] = true;
                        break;
                    case "south":
                        handlesToHide[".mwResizable-s"] = true;
                        handlesToHide[".mwResizable-se"] = true;
                        handlesToHide[".mwResizable-sw"] = true;
                        break;
                    case "east":
                        handlesToHide[".mwResizable-e"] = true;
                        handlesToHide[".mwResizable-ne"] = true;
                        handlesToHide[".mwResizable-se"] = true;
                        break;
                    case "west":
                        handlesToHide[".mwResizable-w"] = true;
                        handlesToHide[".mwResizable-nw"] = true;
                        handlesToHide[".mwResizable-sw"] = true;
                }
            });
            Object.keys(handlesToHide).forEach(function (handleToHide) {
                var handle = query(handleToHide, onTopPane.domNode)[0];
                domStyle.set(handle, "display", "none");
            });
        },

        _computerestrictedEdges: function() {
            var restrictedEdges = [];
            if (this.orientation === "vertical") {
                restrictedEdges.push(this.region === "left" ? "west" : "east");
                if (this.fixParallelDimension) {
                    restrictedEdges.push("north");
                    restrictedEdges.push("south");
                }
            } else {
                restrictedEdges.push(this.region === "top" ? "north" : "south");
                if (this.fixParallelDimension) {
                    restrictedEdges.push("east");
                    restrictedEdges.push("west");
                }
            }
            return restrictedEdges;
        },

        _blockSelectionDuringResize: function (onTopPane) {
            if (onTopPane._resizeHandlers) {
                var blocker = function(node) {
                    SelectionBlocker.blockUntilRelease(node)
                };

                onTopPane._resizeHandlers.forEach(function(handle) {
                    on(handle, touch.press, lang.partial(blocker, handle));
                });
            }
        },

        /**
         * Returns the child at the specified index.
         * @param {Integer} index
         * @returns {Object|null}
         */
        getChildFromIndex: function (index) {
            for (var i = 0; i < this._childInfo.length; i++) {
                if (this._childInfo[i].properties && this._childInfo[i].properties.index === index) {
                    return this._childInfo[i];
                }
            }
            return null;
        },

        /**
         * Returns the first child.
         * @returns {Object|null}
         */
        getFirstChild: function () {
            return this.getChildFromIndex(0);
        },

        /**
         * Returns the next child.
         * @param {Object} widget - reference child
         * @param {Boolean} showing - if true, must return null since a BorderBar won't have a child that is showing
         * @returns {Object|null}
         */
        nextChild: function (widget, showing) {
            return this._findNextChildBasedOnDirection(widget, showing, "forward");
        },

        /**
         * Returns the last child.
         * @returns {Object|null}
         */
        getLastChild: function () {
            return this.getChildFromIndex(this._childInfo.length - 1);
        },

        /**
         * Returns the previous child.
         * @param {Object} widget - reference child
         * @param {Boolean} showing - if true, must return null since a BorderBar won't have a child that is showing
         * @returns {Object|null}
         */
        previousChild: function (widget, showing) {
            return this._findNextChildBasedOnDirection(widget, showing, "backward");
        },

        /**
         * Returns the next child based on the direction.
         * @param {Object} widget - reference child
         * @param {Boolean} showing - if true, must return null since a BorderBar won't have a child that is showing
         * @param {String} direction - "forward" or "backward"
         * @returns {Object|null}
         * @private
         */
        _findNextChildBasedOnDirection: function (widget, showing, direction) {
            if (showing) {
                return null;
            }

            if (!widget) {
                var firstChildBasedOnDirection;
                if (direction === "forward") {
                    firstChildBasedOnDirection = this.getFirstChild();
                } else {
                    firstChildBasedOnDirection = this.getLastChild();
                }
                if (firstChildBasedOnDirection && firstChildBasedOnDirection.properties
                    && firstChildBasedOnDirection.properties.widget) {
                    return firstChildBasedOnDirection.properties.widget;
                }
                else {
                    return firstChildBasedOnDirection;
                }
            }

            var nextChildBasedOnDirection;
            for (var i = 0; i < this._childInfo.length; i++) {
                var widgetChildren = widget.getChildren();
                for (var j = 0; j < widgetChildren.length; j++) {
                    if (this._childInfo[i].widget === widgetChildren[j] && this._childInfo[i].properties) {
                        if (direction === "forward") {
                            nextChildBasedOnDirection = this.getChildFromIndex(this._childInfo[i].properties.index + 1);
                        } else {
                            nextChildBasedOnDirection = this.getChildFromIndex(this._childInfo[i].properties.index - 1);
                        }
                        if (nextChildBasedOnDirection &&  nextChildBasedOnDirection.properties
                            && nextChildBasedOnDirection.properties.widget) {
                            return nextChildBasedOnDirection.properties.widget;
                        }
                    }
                }
            }
            return null;
        },

        _expandAllButtonKeyUpHandler: function (e) {
            if (e.keyCode === keys.SPACE) {
                e.preventDefault();
                this._expandBorderBar();
            }
        },

        _expandAllButtonKeyDownHandler: function (e) {
            if (e.keyCode === keys.ENTER) {
                this._expandBorderBar();
            }
        },

        _expandBorderBar: function () {
            var that = this;
            this._expandAll();
            setTimeout(function() {
                if (that.collapseAllButton) {
                    that.collapseAllButton.tabIndex = -1;
                    that.collapseAllButton.focus();
                }
            }, 400);
        },

        _keyDownHandler: function (e) {
            var keyCode = e.keyCode;
            switch (true) {
                case keyCode === keys.LEFT_ARROW:
                    if (this.orientation === "horizontal") {
                        this._handleNavigation("previous");
                    }
                    break;
                case keyCode === keys.UP_ARROW:
                    if (this.orientation === "vertical") {
                        this._handleNavigation("previous");
                    }
                    break;
                case keyCode === keys.RIGHT_ARROW:
                    if (this.orientation === "horizontal") {
                        this._handleNavigation("next");
                    }
                    break;
                case keyCode === keys.DOWN_ARROW:
                    if (this.orientation === "vertical") {
                        this._handleNavigation("next");
                    }
                    break;
            }
        },

        // Navigation when the side container is collapsed
        _handleNavigation: function (direction) {
            var tabs = [];

            this._buttonInfo.forEach(function (tab) {
                tabs.push(tab.button.domNode);
            });

            if (this.getPostNeighbor) {
                tabs.push(this.getPostNeighbor());
            }

            // Determine the index of the currently focused element
            var focusedNode = document.activeElement;
            var focusedNodeIndex = tabs.indexOf(focusedNode);

            // Collapse the already expanded side panel
            if (focusedNodeIndex !== tabs.length - 1 && this._childInfo[focusedNodeIndex]
                && this._childInfo[focusedNodeIndex].properties && this._childInfo[focusedNodeIndex].properties.get(LayoutChildProperty.IS_SHOWING)) {
                if (this.selectionManager[SelectionManager.SELECTED_CHILD]) {
                    this.selectionManager.setSelected(this.selectionManager[SelectionManager.SELECTED_CHILD], false, true);
                }
            }

            switch (direction) {
                case "next":
                    if (focusedNodeIndex >= 0 && focusedNodeIndex < tabs.length - 1) {
                        tabs[focusedNodeIndex + 1].focus();
                    } else {
                        tabs[0].focus();
                    }
                    break;

                case "previous":
                    if (focusedNodeIndex > 0 && focusedNodeIndex < tabs.length) {
                        tabs[focusedNodeIndex - 1].focus();
                    } else {
                        tabs[tabs.length - 1].focus();
                    }
                    break;
            }
        },

        _buttonKeyDownHandler: function (childInfo, e) {
            var that = this;

            // Escape on the panel button should collapse the temporarily expanded panel.
            if (e.keyCode === keys.ESCAPE) {
                if (childInfo.properties.get(LayoutChildProperty.IS_SHOWING) && childInfo.properties.get(LayoutChildProperty.IS_SELECTED)) {
                    this.selectionManager.blockSuccession();
                    this._collapse(childInfo);
                    setTimeout(function() {
                        that.selectionManager.unblockSuccession();
                    }, 800);
                }
            }

            // Enter on the panel button should expand or collapse the panel.
            if (e.keyCode === keys.ENTER) {
                this._expandCollapsePanel(childInfo);
            }

            // Tab on the panel button should move focus to the panel content.
            if (e.keyCode === keys.TAB) {
                e.preventDefault();
                if (childInfo.properties && childInfo.properties.widget && childInfo.properties.get(LayoutChildProperty.IS_SHOWING)) {
                    if (childInfo.properties.widget.focus) {
                        childInfo.properties.widget.focus();
                    } else if (childInfo.properties.widget.domNode) {
                        childInfo.properties.widget.domNode.focus();
                    }
                }
            }
        },

        _buttonKeyUpHandler: function (childInfo, e) {
            // Space on the panel button should expand or collapse the panel.
            if (e.keyCode === keys.SPACE) {
                e.preventDefault();
                this._expandCollapsePanel(childInfo);
            }
        },

        _expandCollapsePanel: function (childInfo) {
            var that = this;

            if (childInfo.button.domNode) {
                childInfo.button.domNode.tabIndex = -1;
                childInfo.button.domNode.focus();
            }

            if (!childInfo.properties.get(LayoutChildProperty.IS_SHOWING) && !childInfo.properties.get(LayoutChildProperty.IS_SELECTED)) {
                this.selectionManager.blockFocus();

                setTimeout(function() {
                    if (childInfo.properties.get(LayoutChildProperty.IS_SHOWING) && childInfo.properties.get(LayoutChildProperty.IS_SELECTED)) {
                        that.selectionManager.unblockFocus();
                    }
                }, 800);
            } else {
                if (this.selectionManager[SelectionManager.SELECTED_CHILD]) {
                    this.selectionManager.setSelected(this.selectionManager[SelectionManager.SELECTED_CHILD], false, true);
                }
            }
        },

        _createContextMenu: function () {
            var that = this;
            var menu = new ContextMenu({
                id: this.id + "Menu",
                targetNodes: [this.domNode, "#" + this.id + " *"]
            });
            this.own(menu);
            menu.willOpenCallback = function (event) {
                var childInfo = registry.getEnclosingWidget(event.target).childInfo;
                that._populateMenu(menu, childInfo);
            };
        },

        _populateMenu: function (menu, childInfo) {
            var item;
            var that = this;
            // Clear previous items from the menu.  One might expect this to be done on menu close but doing so
            // leads to problems if a menu is closed by a right click gesture that reopens it.
            this._clearMenu(menu);
            if (childInfo) {
                var childProperties = childInfo.properties;

                // Add client supplied items
                if (childProperties.actions) {
                    ActionsMenu._addMenuItems(menu, childProperties.actions);
                    menu.addChild(new MenuSeparator());
                }

                // TAdd close item if closeable
                if (childProperties.closable) {
                    item = new MenuItem({
                        text: lang.replace(layoutL10n.closeSomething, {placeholder: childProperties.shortTitle || childProperties.title}),
                        tag: "close"
                    });
                    on(item.domNode, "click",function () {
                        childProperties.set(LayoutChildProperty.IS_OPEN, false);
                    });
                    menu.addChild(item);
                }
            }
            // Add item to expand all
            item = new MenuItem({
                text: this._getExpandAllItemText(),
                tag: "expandAll"
            });
            on(item.domNode, "click",function () {
                that._expandAll();
            });
            menu.addChild(item);
        },

        _clearMenu: function (menu) {
            menu.getChildren().forEach(function(child) {
                child.destroyRecursive();
            });
        },

        _getExpandAllItemText: function() {
            return this.region === "left" || this.region === "right" ? layoutL10n.expandSidePanel :
                   (this.region === "bottom" ? layoutL10n.expandBottomPanel : layoutL10n.expandTopPanel);

        }
    });

    /**
     * Specifies a node within which clicks should be ignored rather than triggering close of on-top panes.
     * @param node
     * @static
     */
    BorderBar.setCloseClickExclusion = function (node) {
        closeClickExclusion = node;
    };

    return BorderBar;
});
